import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Palette, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import '../styles/ThemeColorModal.css';

interface Theme {
  id: number;
  name: string;
  description: string;
  sidebarBg: string;
  panelHeaderBg: string;
  panelHeaderGradient: string;
  panelHeaderBorder: string;
  panelShadow: string;
  accentColor: string;
  textOnHeader: string;
  actionPreviewBg: string;
  actionPreviewBorder: string;
  actionPreviewText: string;
  actionEditBg: string;
  actionEditBorder: string;
  actionEditText: string;
  actionPrimaryBg: string;
  actionPrimaryBorder: string;
  actionPrimaryText: string;
  actionDangerBg: string;
  actionDangerBorder: string;
  actionDangerText: string;
  contrastPattern?: string;
}

const themes: Theme[] = [
  {
    id: 0,
    name: 'Terra Dogal Toprak',
    description: '#9A7B6F - Danismanlik ve sakin kurumsal ton',
    sidebarBg: 'linear-gradient(135deg, #6F4E37 0%, #7A5A4A 100%)',
    panelHeaderBg: '#9A7B6F',
    panelHeaderGradient: 'linear-gradient(135deg, #b08d80 0%, #9a7b6f 42%, #7c6157 100%)',
    panelHeaderBorder: 'rgba(111, 78, 55, 0.7)',
    panelShadow: '0 16px 34px rgba(111, 78, 55, 0.22), 0 4px 12px rgba(111, 78, 55, 0.18)',
    accentColor: '#7C6157',
    textOnHeader: '#FFFFFF',
    actionPreviewBg: 'linear-gradient(180deg, #FFFFFF 0%, #F7F3F1 100%)',
    actionPreviewBorder: 'rgba(180, 143, 129, 0.78)',
    actionPreviewText: '#5B463C',
    actionEditBg: 'linear-gradient(180deg, #FFFDF8 0%, #FCE7D6 100%)',
    actionEditBorder: 'rgba(217, 119, 6, 0.38)',
    actionEditText: '#9A3412',
    actionPrimaryBg: 'linear-gradient(135deg, #4B5563 0%, #334155 100%)',
    actionPrimaryBorder: 'rgba(51, 65, 85, 0.78)',
    actionPrimaryText: '#FFFFFF',
    actionDangerBg: 'linear-gradient(180deg, #FFF8F8 0%, #FFE4E6 100%)',
    actionDangerBorder: 'rgba(239, 68, 68, 0.34)',
    actionDangerText: '#B42318',
    contrastPattern: 'Terra'
  },
  {
    id: 1,
    name: 'Eco Kurumsal Yesil',
    description: '#16A34A - Ana marka ve surdurulebilirlik odakli',
    sidebarBg: 'linear-gradient(135deg, #14532D 0%, #166534 100%)',
    panelHeaderBg: '#16A34A',
    panelHeaderGradient: 'linear-gradient(135deg, #22c55e 0%, #16a34a 40%, #15803d 100%)',
    panelHeaderBorder: 'rgba(21, 128, 61, 0.72)',
    panelShadow: '0 16px 34px rgba(21, 128, 61, 0.22), 0 4px 12px rgba(21, 128, 61, 0.18)',
    accentColor: '#15803D',
    textOnHeader: '#FFFFFF',
    actionPreviewBg: 'linear-gradient(180deg, #FFFFFF 0%, #F3FAF5 100%)',
    actionPreviewBorder: 'rgba(21, 128, 61, 0.24)',
    actionPreviewText: '#14532D',
    actionEditBg: 'linear-gradient(180deg, #FFFDF8 0%, #FCE7D6 100%)',
    actionEditBorder: 'rgba(217, 119, 6, 0.38)',
    actionEditText: '#9A3412',
    actionPrimaryBg: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
    actionPrimaryBorder: 'rgba(30, 41, 59, 0.82)',
    actionPrimaryText: '#FFFFFF',
    actionDangerBg: 'linear-gradient(180deg, #FFF8F8 0%, #FFE4E6 100%)',
    actionDangerBorder: 'rgba(239, 68, 68, 0.34)',
    actionDangerText: '#B42318',
    contrastPattern: 'Eco'
  },
  {
    id: 2,
    name: 'Blue Kurumsal Mavi',
    description: '#2563EB - Guvenilir ve profesyonel raporlama tonu',
    sidebarBg: 'linear-gradient(135deg, #1E3A8A 0%, #1D4ED8 100%)',
    panelHeaderBg: '#2563EB',
    panelHeaderGradient: 'linear-gradient(135deg, #4f83ff 0%, #2563eb 40%, #1d4ed8 100%)',
    panelHeaderBorder: 'rgba(29, 78, 216, 0.7)',
    panelShadow: '0 16px 34px rgba(29, 78, 216, 0.22), 0 4px 12px rgba(29, 78, 216, 0.18)',
    accentColor: '#1D4ED8',
    textOnHeader: '#FFFFFF',
    actionPreviewBg: 'linear-gradient(180deg, #FFFFFF 0%, #EFF6FF 100%)',
    actionPreviewBorder: 'rgba(37, 99, 235, 0.24)',
    actionPreviewText: '#1E3A8A',
    actionEditBg: 'linear-gradient(180deg, #FFFDF8 0%, #FCE7D6 100%)',
    actionEditBorder: 'rgba(217, 119, 6, 0.38)',
    actionEditText: '#9A3412',
    actionPrimaryBg: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
    actionPrimaryBorder: 'rgba(30, 41, 59, 0.82)',
    actionPrimaryText: '#FFFFFF',
    actionDangerBg: 'linear-gradient(180deg, #FFF8F8 0%, #FFE4E6 100%)',
    actionDangerBorder: 'rgba(239, 68, 68, 0.34)',
    actionDangerText: '#B42318',
    contrastPattern: 'Blue'
  },
  {
    id: 3,
    name: 'Graph Notr Grafit',
    description: '#475569 - Veri agirlikli ve sade kurumsal tema',
    sidebarBg: 'linear-gradient(135deg, #111827 0%, #334155 100%)',
    panelHeaderBg: '#475569',
    panelHeaderGradient: 'linear-gradient(135deg, #64748b 0%, #475569 42%, #334155 100%)',
    panelHeaderBorder: 'rgba(51, 65, 85, 0.74)',
    panelShadow: '0 16px 34px rgba(15, 23, 42, 0.24), 0 4px 12px rgba(15, 23, 42, 0.18)',
    accentColor: '#334155',
    textOnHeader: '#F8FAFC',
    actionPreviewBg: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
    actionPreviewBorder: 'rgba(148, 163, 184, 0.72)',
    actionPreviewText: '#1F2937',
    actionEditBg: 'linear-gradient(180deg, #FFFDF8 0%, #FCE7D6 100%)',
    actionEditBorder: 'rgba(217, 119, 6, 0.38)',
    actionEditText: '#9A3412',
    actionPrimaryBg: 'linear-gradient(135deg, #0F172A 0%, #1F2937 100%)',
    actionPrimaryBorder: 'rgba(51, 65, 85, 0.84)',
    actionPrimaryText: '#FFFFFF',
    actionDangerBg: 'linear-gradient(180deg, #FFF8F8 0%, #FFE4E6 100%)',
    actionDangerBorder: 'rgba(239, 68, 68, 0.34)',
    actionDangerText: '#B42318',
    contrastPattern: 'Graph'
  }
];

const normalizeThemeId = (rawThemeId: number | null | undefined) => {
  switch (rawThemeId) {
    case 0:
      return 0;
    case 1:
    case 6:
    case 8:
      return 1;
    case 2:
      return 2;
    case 3:
    case 4:
    case 5:
    case 7:
      return 3;
    default:
      return 0;
  }
};

const StylesPanel: React.FC = () => {
  const { profile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTheme, setActiveTheme] = useState(0);
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>('light');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const modalRoot = useMemo(() => (typeof document !== 'undefined' ? document.body : null), []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const updateSystemTheme = () => {
      setSystemTheme(mediaQuery.matches ? 'dark' : 'light');
    };

    updateSystemTheme();
    mediaQuery.addEventListener('change', updateSystemTheme);
    return () => mediaQuery.removeEventListener('change', updateSystemTheme);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!profile?.id) {
      const savedTheme = localStorage.getItem('theme_id');
      const themeId = normalizeThemeId(savedTheme ? parseInt(savedTheme, 10) : 0);
      setActiveTheme(themeId);
      applyTheme(themeId);
      return;
    }

    void loadThemeFromDatabase();
  }, [profile?.id]);

  const loadThemeFromDatabase = async () => {
    try {
      setLoading(true);

      if (!profile?.id) {
        const savedTheme = localStorage.getItem('theme_id');
        const themeId = normalizeThemeId(savedTheme ? parseInt(savedTheme, 10) : 0);
        setActiveTheme(themeId);
        applyTheme(themeId);
        return;
      }

      const { data, error: dbError } = await supabase
        .from('user_theme_preferences')
        .select('theme_id')
        .eq('user_id', profile.id)
        .maybeSingle();

      if (dbError) {
        const savedTheme = localStorage.getItem('theme_id');
        const themeId = normalizeThemeId(savedTheme ? parseInt(savedTheme, 10) : 0);
        setActiveTheme(themeId);
        applyTheme(themeId);
        setError('Veritabani baglantisi basarisiz, yerel depolama kullaniliyor.');
        return;
      }

      const themeId = normalizeThemeId(data?.theme_id ?? 0);
      setActiveTheme(themeId);
      applyTheme(themeId);
      localStorage.setItem('theme_id', themeId.toString());
      setError(null);
    } catch (error) {
      console.error('[StylesPanel] Theme load failed, falling back to localStorage', error);
      const savedTheme = localStorage.getItem('theme_id');
      const themeId = normalizeThemeId(savedTheme ? parseInt(savedTheme, 10) : 0);
      setActiveTheme(themeId);
      applyTheme(themeId);
      setError('Tema yuklenemedi, yerel depolama kullaniliyor.');
    } finally {
      setLoading(false);
    }
  };

  const applyTheme = (themeId: number) => {
    const normalizedThemeId = normalizeThemeId(themeId);
    const theme = themes.find((item) => item.id === normalizedThemeId);
    if (!theme) {
      setError(`Tema bulunamadi: ${themeId}`);
      return;
    }

    const root = document.documentElement;
    const body = document.body;
    root.style.setProperty('--sidebar-bg', theme.sidebarBg);
    root.style.setProperty('--panel-header-bg', theme.panelHeaderBg);
    root.style.setProperty('--panel-header-gradient', theme.panelHeaderGradient);
    root.style.setProperty('--panel-header-border', theme.panelHeaderBorder);
    root.style.setProperty('--panel-shadow', theme.panelShadow);
    root.style.setProperty('--accent-color', theme.accentColor);
    root.style.setProperty('--text-on-header', theme.textOnHeader);
    root.style.setProperty('--action-preview-bg', theme.actionPreviewBg);
    root.style.setProperty('--action-preview-border', theme.actionPreviewBorder);
    root.style.setProperty('--action-preview-text', theme.actionPreviewText);
    root.style.setProperty('--action-edit-bg', theme.actionEditBg);
    root.style.setProperty('--action-edit-border', theme.actionEditBorder);
    root.style.setProperty('--action-edit-text', theme.actionEditText);
    root.style.setProperty('--action-primary-bg', theme.actionPrimaryBg);
    root.style.setProperty('--action-primary-border', theme.actionPrimaryBorder);
    root.style.setProperty('--action-primary-text', theme.actionPrimaryText);
    root.style.setProperty('--action-danger-bg', theme.actionDangerBg);
    root.style.setProperty('--action-danger-border', theme.actionDangerBorder);
    root.style.setProperty('--action-danger-text', theme.actionDangerText);
    root.style.setProperty('--border-color', theme.sidebarBg.split('(')[1]?.split(' ')[0] || theme.panelHeaderBg);
    root.classList.remove(...themes.map((item) => `theme-${item.id}`));
    root.classList.add(`theme-${normalizedThemeId}`);
    body.classList.remove(...themes.map((item) => `theme-${item.id}`));
    body.classList.add(`theme-${normalizedThemeId}`);
    root.setAttribute('data-theme-id', String(normalizedThemeId));

    const sidebar = document.querySelector('.sidebar-container.improved');
    if (sidebar) {
      (sidebar as HTMLElement).style.setProperty('--sidebar-bg', theme.sidebarBg, 'important');
      (sidebar as HTMLElement).style.setProperty('--accent-color', theme.accentColor, 'important');
    }

    setError(null);
  };

  const saveThemeToDatabase = async (themeId: number) => {
    if (!profile?.id) {
      localStorage.setItem('theme_id', normalizeThemeId(themeId).toString());
      return;
    }

    try {
      const { error: dbError } = await supabase
        .from('user_theme_preferences')
        .upsert(
          {
            user_id: profile.id,
            theme_id: normalizeThemeId(themeId),
            updated_at: new Date().toISOString()
          },
          { onConflict: 'user_id' }
        );

      if (dbError) {
        throw dbError;
      }

      localStorage.setItem('theme_id', normalizeThemeId(themeId).toString());
      setError(null);
    } catch {
      localStorage.setItem('theme_id', normalizeThemeId(themeId).toString());
      setError('Tema kaydedildi, yerel depolama kullaniliyor.');
      window.setTimeout(() => setError(null), 3000);
    }
  };

  const handleThemeChange = (themeId: number) => {
    setActiveTheme(themeId);
    applyTheme(themeId);
    void saveThemeToDatabase(themeId);
  };

  const modalContent = modalRoot ? createPortal(
    <>
      <div className="theme-modal-overlay" onClick={() => setIsOpen(false)} aria-hidden="true" />
      <div className="theme-modal-container" role="dialog" aria-modal="true" aria-labelledby="theme-modal-title">
        <div className="theme-modal-header">
          <div className="flex items-center gap-2">
            <Palette size={18} className="text-emerald-700" />
            <h3 id="theme-modal-title">Tema Secimi</h3>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Tema secim penceresini kapat"
          >
            <X size={16} />
          </button>
        </div>

        {error && (
          <div className="theme-modal-error">
            <p className="text-sm">Warning: {error}</p>
          </div>
        )}

        <div className="theme-modal-content">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          )}

          {!loading && themes.map((theme) => (
            <button
              key={theme.id}
              onClick={() => handleThemeChange(theme.id)}
              disabled={loading}
              className={`theme-modal-button ${activeTheme === theme.id ? 'active' : ''}`}
              title={theme.description}
            >
              <div className="flex items-center gap-3 w-full">
                <div className="flex gap-1.5 flex-shrink-0">
                  <div
                    className="w-6 h-6 rounded-md border-2 border-white shadow-sm"
                    style={{
                      background: theme.sidebarBg,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                    }}
                  />
                  <div
                    className="w-6 h-6 rounded-md border-2 border-white shadow-sm"
                    style={{
                      background: theme.panelHeaderBg,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                    }}
                  />
                </div>

                <div className="flex-1 text-left min-w-0">
                  <div className="text-sm font-semibold text-gray-800 truncate flex items-center gap-1.5">
                    <span className="text-base">{theme.contrastPattern}</span>
                    <span>{theme.name}</span>
                  </div>
                  <div className="text-xs text-gray-500 truncate">{theme.description}</div>
                </div>

                {activeTheme === theme.id && (
                  <div className="text-emerald-600 font-bold text-sm flex-shrink-0">Secili</div>
                )}
              </div>
            </button>
          ))}
        </div>

        <div className="theme-modal-footer">
          <p>Secilen stil tum sayfalarda uygulanir</p>
          <p className="mt-1">Sistem: {systemTheme === 'dark' ? 'Koyu' : 'Acik'}</p>
        </div>
      </div>
    </>,
    modalRoot
  ) : null;

  if (!isOpen) {
    return (
      <button
        onClick={() => {
          setIsOpen(true);
          setError(null);
        }}
        className="flex items-center justify-center w-10 h-10 rounded-lg bg-white border-2 border-stone-200 hover:border-stone-400 hover:bg-stone-50 text-stone-700 hover:text-stone-900 shadow-sm hover:shadow-md transition-all transform hover:scale-105"
        title="Tema Secimi"
      >
        <Palette size={20} />
      </button>
    );
  }

  return modalContent;
};

export default StylesPanel;
